import { useState, useEffect } from 'react'
import type React from 'react'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import styled from 'styled-components'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import TopBar from './TopBar'

const PAGE_META: Record<string, { title: string; subtitle?: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'System overview' },
  '/organisations': { title: 'Organizations', subtitle: 'Manage all organizations' },
  '/plans': { title: 'Plans', subtitle: 'Manage subscription plans' },
  '/billing': { title: 'Billing', subtitle: 'Billing details' },
  '/feature-flags': { title: 'Feature Flags', subtitle: 'Toggle features across plans' },
  '/notifications': { title: 'Notifications', subtitle: 'Manage alerts and notification events' },
  '/usage': { title: 'Usage', subtitle: 'Platform usage metrics' },
  '/analytics': { title: 'Analytics', subtitle: 'Growth and engagement' },
  '/upgrade-ops': { title: 'Upgrade Ops', subtitle: 'Pending upgrade operations' },
  '/auditLogs': { title: 'Audit Logs', subtitle: 'System activity log' },
  '/security': { title: 'Security', subtitle: 'Access and security settings' },
  '/roles': { title: 'Roles', subtitle: 'Manage roles and permissions' },
  '/user-management': { title: 'User Management', subtitle: 'Manage admin users' },
  '/role-management': { title: 'Role Management', subtitle: 'Configure role permissions' },
}

const LayoutRoot = styled(Box)`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: #f4f6fb;
`

const ContentShell = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
`

const MainContent = styled(Box)`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const DashboardLayout = (): React.JSX.Element => {
  const { pathname } = useLocation()
  const isSmallScreen = useMediaQuery('(max-width: 768px)')
  const [manualOverride, setManualOverride] = useState<boolean | null>(null)

  // Auto-collapse on small screens unless user has manually toggled
  useEffect(() => {
    setManualOverride(null)
  }, [isSmallScreen])

  const sidebarOpen = manualOverride !== null ? manualOverride : !isSmallScreen

  const handleMenuClick = () => setManualOverride((prev) => !(prev !== null ? prev : !isSmallScreen))

  const isPlanEditPath = /^\/plans\/[^/]+\/edit$/.test(pathname)
  const isOrganisationDetailPath = /^\/organisations\/[^/]+$/.test(pathname)
  const isOrganisationEditPath = /^\/organisations\/[^/]+\/edit$/.test(pathname)

  let meta = PAGE_META[pathname] ?? { title: 'PlanIT Admin' }
  if (isPlanEditPath) {
    meta = { title: 'Edit Plan', subtitle: 'Update plan details and feature settings' }
  } else if (isOrganisationEditPath) {
    meta = { title: 'Edit Organization', subtitle: 'Review and update organisation settings' }
  } else if (isOrganisationDetailPath) {
    meta = { title: 'Organization Detail', subtitle: 'Review organization details and controls' }
  }

  return (
    <LayoutRoot>
      <Sidebar open={sidebarOpen} />
      <ContentShell>
        <TopBar
          title={meta.title}
          {...(meta.subtitle ? { subtitle: meta.subtitle } : {})}
          onMenuClick={handleMenuClick}
        />
        <MainContent>
          <Outlet />
        </MainContent>
      </ContentShell>
    </LayoutRoot>
  )
}

export default DashboardLayout
